import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { DashboardEmailFilter } from '../../components/common/DashboardEmailFilter';
import { DashboardImageControls } from '../../components/common/DashboardImageControls';
import { DashboardImagePreview } from '../../components/common/DashboardImagePreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { ReviewStatusControl } from '../../components/common/ReviewStatusControl';
import { useDashboardImageSettings } from '../../hooks/useDashboardImageSettings';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  BarcodeStatus,
  ForgeDesignDashboardRow,
  ForgeDesignsChainSnapshot,
  ForgeDesignsDashboardResponse,
  ForgeReviewSort,
} from '../../types';

const PAGE_LIMIT = 50;
const DESIGN_STATUS_OPTIONS: Array<{ value: BarcodeStatus; label: string }> = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
];

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function statusLabel(status: string): string {
  if (status === 'confirmed') return 'Confirmed';
  if (status === 'pending') return 'Pending';
  if (status === 'unconfirmed') return 'Unconfirmed';
  return status || 'Unknown';
}

function statusClass(status: string): string {
  if (status === 'confirmed') return 'is-full';
  if (status === 'pending') return 'is-partial';
  return 'is-non-front';
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function totalDesigns(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.designCount, 0);
}

function totalConfirmed(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.confirmedCount, 0);
}

function totalPending(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.pendingCount, 0);
}

function totalUnconfirmed(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.unconfirmedCount, 0);
}

function totalWithImages(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.withImageCount, 0);
}

function totalReviewed(chains: ForgeDesignsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.reviewedCount, 0);
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value);
}

type DesignsDashboardUserOptionAliases = ForgeDesignsDashboardResponse & {
  updated_by_options?: string[];
};

type DashboardImageHandlers = Pick<
  ReturnType<typeof useDashboardImageSettings>,
  'handleImageHover' | 'handleImageMove' | 'handleImageLeave'
>;

function designZoomMeta(design: ForgeDesignDashboardRow): ZoomMeta {
  return {
    metadataType: 'design',
    id: design.designId,
    normalizedBarcode: design.normalizedBarcode ?? undefined,
    barcodeStatus: design.barcodeStatus,
    cropCount: design.cropCount,
    noteCount: design.noteCount,
    createdAt: design.createdAt ?? undefined,
    updatedAt: design.updatedAt ?? undefined,
    updatedBy: design.updatedBy ?? undefined,
  };
}

function updatedByOptionsFromDesigns(dashboard: ForgeDesignsDashboardResponse | null): string[] {
  const snakeOptions = (dashboard as DesignsDashboardUserOptionAliases | null)?.updated_by_options;
  const rowOptions =
    dashboard?.designs.map((design) => design.updatedBy).filter(isNonEmptyString) ?? [];
  return [
    ...new Set([...(dashboard?.updatedByOptions ?? []), ...(snakeOptions ?? []), ...rowOptions]),
  ].sort();
}

function mergeChainRefresh(
  current: ForgeDesignsDashboardResponse,
  refreshed: ForgeDesignsDashboardResponse,
  replaceDesigns: boolean
): ForgeDesignsDashboardResponse {
  const refreshedChainsById = new Map(
    refreshed.chains.map((chain) => [chain.chain.chainId, chain])
  );
  const currentChainIds = new Set(current.chains.map((chain) => chain.chain.chainId));
  const chains = [
    ...current.chains.map((chain) => refreshedChainsById.get(chain.chain.chainId) ?? chain),
    ...refreshed.chains.filter((chain) => !currentChainIds.has(chain.chain.chainId)),
  ].sort((left, right) => left.chain.chainId - right.chain.chainId);

  return {
    ...current,
    computedAtUtc: refreshed.computedAtUtc,
    supportedChainIds: refreshed.supportedChainIds,
    chains,
    designs: replaceDesigns ? refreshed.designs : current.designs,
    total: replaceDesigns ? refreshed.total : current.total,
    page: replaceDesigns ? refreshed.page : current.page,
    limit: replaceDesigns ? refreshed.limit : current.limit,
    hasNext: replaceDesigns ? refreshed.hasNext : current.hasNext,
  };
}

interface StatusPillProps {
  status: string;
}

const StatusPill: React.FC<StatusPillProps> = ({ status }) => (
  <span className={`dg-scope-pill ${statusClass(status)}`}>{statusLabel(status)}</span>
);

interface ReviewPillProps {
  isReviewed: boolean;
}

const ReviewPill: React.FC<ReviewPillProps> = ({ isReviewed }) => (
  <span className={`dg-scope-pill ${isReviewed ? 'is-reviewed' : 'is-unreviewed'}`}>
    {isReviewed ? 'Reviewed' : 'Needs review'}
  </span>
);

interface ChainCardProps {
  chain: ForgeDesignsChainSnapshot;
  isSelected: boolean;
  isRefreshing: boolean;
  onSelect: () => void;
  onRefresh: () => void;
}

const ChainCard: React.FC<ChainCardProps> = ({
  chain,
  isSelected,
  isRefreshing,
  onSelect,
  onRefresh,
}) => (
  <article className={`dg-chain-card ${isSelected ? 'is-selected' : ''}`}>
    <button type="button" className="dg-chain-select" onClick={onSelect}>
      <span className="dg-chain-title">{chain.chain.chainName}</span>
      <span className="dg-chain-subtitle">chain {chain.chain.chainId}</span>
      <span className="dg-chain-totals">
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.designCount)}</strong>
          designs
        </span>
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.creatorCount)}</strong>
          users
        </span>
      </span>
    </button>
    <div className="dg-chain-bars">
      <span className="dg-chain-bar is-full">{formatNumber(chain.confirmedCount)}</span>
      <span className="dg-chain-bar is-partial">{formatNumber(chain.pendingCount)}</span>
      <span className="dg-chain-bar is-non-front">{formatNumber(chain.unconfirmedCount)}</span>
    </div>
    <button
      type="button"
      className="dg-icon-button"
      aria-label={`Refresh ${chain.chain.chainName}`}
      title={`Refresh ${chain.chain.chainName}`}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
    </button>
  </article>
);

interface DesignPreviewProps {
  design: ForgeDesignDashboardRow;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
}

const DesignPreview: React.FC<DesignPreviewProps> = ({ design, imageSize, imageHandlers }) => (
  <div className="dg-thumb-strip" aria-label="Design preview">
    <DashboardImagePreview
      imageUrl={design.previewVizUrl}
      fallback={shortId(design.designId)}
      size={imageSize}
      className="dg-thumb"
      title={design.normalizedBarcode ?? design.designId}
      meta={designZoomMeta(design)}
      onImageHover={imageHandlers.handleImageHover}
      onImageMove={imageHandlers.handleImageMove}
      onImageLeave={imageHandlers.handleImageLeave}
    />
  </div>
);

interface DesignRowProps {
  design: ForgeDesignDashboardRow;
  isSelected: boolean;
  isReviewing: boolean;
  canReview: boolean;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
  onSelect: () => void;
  onToggleReview: () => void;
}

const DesignRow: React.FC<DesignRowProps> = ({
  design,
  isSelected,
  isReviewing,
  canReview,
  imageSize,
  imageHandlers,
  onSelect,
  onToggleReview,
}) => (
  <tr
    className={`dg-group-row ${isSelected ? 'is-selected' : ''}`}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <td className="dg-row-examples">
      <DesignPreview design={design} imageSize={imageSize} imageHandlers={imageHandlers} />
    </td>
    <td className="dg-row-id" title={design.designId}>
      {design.forgeUrl ? (
        <MasterLabelLink
          className="dg-id-link"
          to={design.forgeUrl}
          title={`Open design ${design.designId} in Master Label`}
          onClick={(event) => event.stopPropagation()}
        >
          {shortId(design.designId)}
        </MasterLabelLink>
      ) : (
        shortId(design.designId)
      )}
    </td>
    <td className="dg-row-count" title={design.normalizedBarcode ?? undefined}>
      {design.normalizedBarcode ?? '-'}
    </td>
    <td className="dg-row-count">{formatNumber(design.barcodeDesignCount)}</td>
    <td className="dg-row-count" title={design.itemNumber ?? undefined}>
      {design.itemNumber ? formatNumber(design.itemNumberDesignCount) : '-'}
    </td>
    <td className="dg-row-type">
      <StatusPill status={design.barcodeStatus} />
    </td>
    <td className="dg-row-hierarchy">{design.chainName}</td>
    <td className="dg-row-timestamp">{formatTimestamp(design.createdAt)}</td>
    <td className="dg-row-user" title={design.updatedBy ?? undefined}>
      {design.updatedBy ?? '-'}
    </td>
    <td className="dg-row-count">{formatNumber(design.cropCount)}</td>
    <td className="dg-row-reviewed-cell dg-row-reviewed-cell--last">
      <ReviewStatusControl
        isReviewed={design.isReviewed}
        isBusy={isReviewing}
        canToggle={canReview}
        reviewedLabel="Clear design review"
        unreviewedLabel="Mark design reviewed"
        reviewedBy={design.reviewedBy}
        reviewedAt={design.reviewedAt}
        testId={`design-review-button-${design.designId}`}
        onToggle={(event) => {
          event.stopPropagation();
          onToggleReview();
        }}
      />
    </td>
  </tr>
);

interface DesignPanelProps {
  design: ForgeDesignDashboardRow;
  imageSize: number;
  imageHandlers: DashboardImageHandlers;
}

const DesignPanel: React.FC<DesignPanelProps> = ({ design, imageSize, imageHandlers }) => (
  <div className="dg-inline-panel dg-hierarchy-panel">
    <div className="dg-panel-title">
      <StyleOutlinedIcon />
      <span className="dg-panel-title-text" title={`Design ${design.designId}`}>
        Design {design.designId}
      </span>
    </div>
    <div className="dg-design-preview">
      <DashboardImagePreview
        imageUrl={design.previewVizUrl}
        fallback={shortId(design.designId)}
        size={Math.max(imageSize, 54)}
        title={design.normalizedBarcode ?? design.designId}
        meta={designZoomMeta(design)}
        onImageHover={imageHandlers.handleImageHover}
        onImageMove={imageHandlers.handleImageMove}
        onImageLeave={imageHandlers.handleImageLeave}
      />
      <div>
        <StatusPill status={design.barcodeStatus} />
        <strong>{design.normalizedBarcode ?? shortId(design.designId)}</strong>
        <span>{design.chainName}</span>
      </div>
    </div>
    <div className="dg-selected-summary">
      <span>Created</span>
      <strong>{formatTimestamp(design.createdAt)}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Updated by</span>
      <strong title={design.updatedBy ?? undefined}>{design.updatedBy ?? '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Designs per barcode</span>
      <strong>{formatNumber(design.barcodeDesignCount)}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Item number</span>
      <strong title={design.itemNumber ?? undefined}>{design.itemNumber ?? '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>Designs per item</span>
      <strong>{design.itemNumber ? formatNumber(design.itemNumberDesignCount) : '-'}</strong>
    </div>
    <div className="dg-selected-summary">
      <span>{design.isReviewed ? 'Reviewed by' : 'Review'}</span>
      <ReviewPill isReviewed={design.isReviewed} />
      <strong title={design.reviewedAt ?? undefined}>
        {design.isReviewed ? design.reviewedBy || 'reviewed' : 'pending'}
      </strong>
    </div>
    <div className="dg-tree">
      <div className="dg-tree-current">
        <span>{formatNumber(design.cropCount)} crops</span>
        <strong>{formatNumber(design.noteCount)} notes</strong>
      </div>
      <div className="dg-tree-current">
        <span>Images</span>
        <strong>
          {[
            design.hasFrontImage ? 'front' : null,
            design.hasBackImage ? 'back' : null,
            design.hasBarcodeImage ? 'barcode' : null,
          ]
            .filter(Boolean)
            .join(', ') || 'none'}
        </strong>
      </div>
    </div>
    {design.forgeUrl ? (
      <MasterLabelLink className="dg-secondary-button dg-panel-link" to={design.forgeUrl}>
        <OpenInNewOutlinedIcon />
        Open in Forge
      </MasterLabelLink>
    ) : null}
  </div>
);

export const DesignsDashboard: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeDesignsDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedChainId, setSelectedChainId] = useState<number | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<BarcodeStatus | 'all'>('all');
  const [queryInput, setQueryInput] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const [selectedUpdatedBy, setSelectedUpdatedBy] = useState<string[]>([]);
  const [reviewSort, setReviewSort] = useState<ForgeReviewSort>('newest');
  const [page, setPage] = useState(1);
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [reviewingDesignIds, setReviewingDesignIds] = useState<Set<string>>(() => new Set());
  const loadRequestSequence = useRef(0);
  const imageSettings = useDashboardImageSettings('forge-designs-dashboard-crop-size', 42);

  const viewKey = `${selectedChainId}|${selectedStatus}|${committedQuery}|${selectedUpdatedBy.join('\u0000')}|${reviewSort}|${page}|${selectedDesignId ?? ''}`;
  const viewKeyRef = useRef(viewKey);

  useEffect(() => {
    viewKeyRef.current = viewKey;
  }, [viewKey]);

  const loadDashboard = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextDashboard = await ForgeApiService.fetchDesignsDashboard(token, {
        chainId: selectedChainId === 'all' ? null : selectedChainId,
        barcodeStatus: selectedStatus === 'all' ? null : selectedStatus,
        query: committedQuery || null,
        updatedByValues: selectedUpdatedBy,
        reviewSort,
        page,
        limit: PAGE_LIMIT,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setDashboard(nextDashboard);
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Designs';
      setDashboard(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) {
        setIsLoading(false);
      }
    }
  }, [
    committedQuery,
    page,
    reviewSort,
    selectedChainId,
    selectedStatus,
    selectedUpdatedBy,
    showErrorToast,
    token,
  ]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const reloadCurrentDashboardSlice = async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    const nextDashboard = await ForgeApiService.fetchDesignsDashboard(token, {
      chainId: selectedChainId === 'all' ? null : selectedChainId,
      barcodeStatus: selectedStatus === 'all' ? null : selectedStatus,
      query: committedQuery || null,
      updatedByValues: selectedUpdatedBy,
      reviewSort,
      page,
      limit: PAGE_LIMIT,
    });
    if (loadRequestSequence.current !== requestSequence) return;
    setDashboard(nextDashboard);
    setLoadError(null);
  };

  useEffect(() => {
    if (!dashboard?.designs.length) {
      setSelectedDesignId(null);
      return;
    }
    if (
      selectedDesignId &&
      !dashboard.designs.some((design) => design.designId === selectedDesignId)
    ) {
      setSelectedDesignId(null);
    }
  }, [dashboard?.designs, selectedDesignId]);

  const chainOptions = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);
  const updatedByOptions = useMemo(() => updatedByOptionsFromDesigns(dashboard), [dashboard]);
  const isRefreshingAnyChain = refreshingChainIds.size > 0;

  const refreshRequest = (chainId: number) => ({
    chain_id: chainId,
    barcode_status: selectedStatus === 'all' ? null : selectedStatus,
    query: committedQuery || null,
    updated_by_values: selectedUpdatedBy,
    review_sort: reviewSort,
    page,
    limit: PAGE_LIMIT,
  });

  const handleRefreshChain = async (chainId: number) => {
    const requestViewKey = viewKeyRef.current;
    setRefreshingChainIds((current) => new Set(current).add(chainId));
    try {
      const refreshed = await ForgeApiService.refreshDesignsDashboard(
        token,
        refreshRequest(chainId)
      );
      if (viewKeyRef.current !== requestViewKey) {
        return;
      }
      setDashboard((current) =>
        current ? mergeChainRefresh(current, refreshed, selectedChainId === chainId) : refreshed
      );
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing Designs');
    } finally {
      setRefreshingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
    }
  };

  const handleToggleDesignReview = async (design: ForgeDesignDashboardRow) => {
    if (!hasInternalAccess) return;
    const nextIsReviewed = !design.isReviewed;
    setReviewingDesignIds((current) => new Set(current).add(design.designId));
    try {
      await ForgeApiService.updateDesignReviewStatus(token, design.designId, {
        is_reviewed: nextIsReviewed,
      });
      await reloadCurrentDashboardSlice();
      showSuccessToast(nextIsReviewed ? 'Design marked reviewed' : 'Design review cleared');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error updating Design review status'
      );
    } finally {
      setReviewingDesignIds((current) => {
        const next = new Set(current);
        next.delete(design.designId);
        return next;
      });
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setCommittedQuery(queryInput.trim());
  };

  const handleSelectChain = (chainId: number | 'all') => {
    setSelectedChainId(chainId);
    setPage(1);
  };

  const handleSelectStatus = (status: BarcodeStatus | 'all') => {
    setSelectedStatus(status);
    setPage(1);
  };

  const clearSearch = () => {
    setQueryInput('');
    setCommittedQuery('');
    setSelectedUpdatedBy([]);
    setReviewSort('newest');
    setPage(1);
  };

  const handleSelectUpdatedBy = (nextSelected: string[]) => {
    setSelectedUpdatedBy(nextSelected);
    setPage(1);
  };

  const handleReviewSortChange = (nextSort: ForgeReviewSort) => {
    setReviewSort(nextSort);
    setPage(1);
  };

  const hasListFilters =
    Boolean(committedQuery) || selectedUpdatedBy.length > 0 || reviewSort !== 'newest';

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-main">
          <div>
            <div className="dg-dashboard-title">Designs</div>
            <div className="dg-hero-metrics">
              <span>
                <strong>{formatNumber(totalDesigns(dashboard?.chains ?? []))}</strong>
                designs
              </span>
              <span>
                <strong>{formatNumber(totalConfirmed(dashboard?.chains ?? []))}</strong>
                confirmed
              </span>
              <span>
                <strong>{formatNumber(totalPending(dashboard?.chains ?? []))}</strong>
                pending
              </span>
              <span>
                <strong>{formatNumber(totalUnconfirmed(dashboard?.chains ?? []))}</strong>
                unconfirmed
              </span>
              <span>
                <strong>{formatNumber(totalWithImages(dashboard?.chains ?? []))}</strong>
                with images
              </span>
              <span>
                <strong>{formatNumber(totalReviewed(dashboard?.chains ?? []))}</strong>
                reviewed
              </span>
            </div>
          </div>
        </div>
        <div className="dg-filter-row">
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Designs chain filter"
              value={selectedChainId}
              onChange={(event) =>
                handleSelectChain(event.target.value === 'all' ? 'all' : Number(event.target.value))
              }
            >
              <option value="all">All supported chains</option>
              {chainOptions.map((chain) => (
                <option key={chain.chain.chainId} value={chain.chain.chainId}>
                  {chain.chain.chainName}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Designs status filter"
              value={selectedStatus}
              onChange={(event) =>
                handleSelectStatus(
                  event.target.value === 'all' ? 'all' : (event.target.value as BarcodeStatus)
                )
              }
            >
              <option value="all">All statuses</option>
              {DESIGN_STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={isLoading || isRefreshingAnyChain}
            onClick={() => void loadDashboard()}
          >
            <RefreshOutlinedIcon className={isLoading ? 'is-spinning' : ''} />
            Refresh view
          </button>
          <DashboardImageControls
            label="Designs"
            cropSize={imageSettings.cropSize}
            onCropSizeChange={imageSettings.setCropSize}
            zoomEnabled={imageSettings.zoomEnabled}
            onToggleZoom={imageSettings.toggleZoom}
          />
        </div>
      </section>

      <section className="dg-chain-grid" aria-label="Designs chain stats">
        {chainOptions.map((chain) => (
          <ChainCard
            key={chain.chain.chainId}
            chain={chain}
            isSelected={selectedChainId === chain.chain.chainId}
            isRefreshing={refreshingChainIds.has(chain.chain.chainId)}
            onSelect={() => handleSelectChain(chain.chain.chainId)}
            onRefresh={() => void handleRefreshChain(chain.chain.chainId)}
          />
        ))}
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Designs</div>
              <div className="dg-list-count">
                Showing{' '}
                {visibleRange(
                  dashboard?.page ?? page,
                  dashboard?.limit ?? PAGE_LIMIT,
                  dashboard?.total ?? 0
                )}{' '}
                of {formatNumber(dashboard?.total ?? 0)}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!dashboard?.hasNext || isLoading}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
            <form className="dg-list-controls" onSubmit={handleSearchSubmit}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search Designs"
                  placeholder="Design ID or barcode"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              <DashboardEmailFilter
                ariaLabel="Filter Designs by updated user"
                options={updatedByOptions}
                selected={selectedUpdatedBy}
                onChange={handleSelectUpdatedBy}
              />
              <label className="dg-sort-control">
                <span>Sort</span>
                <select
                  aria-label="Sort Designs"
                  value={reviewSort}
                  onChange={(event) =>
                    handleReviewSortChange(event.target.value as ForgeReviewSort)
                  }
                >
                  <option value="newest">Newest</option>
                  <option value="barcode_design_count_desc">Most designs per barcode</option>
                  <option value="unreviewed_first">Not reviewed first</option>
                  <option value="reviewed_first">Reviewed first</option>
                </select>
              </label>
              {hasListFilters ? (
                <button type="button" className="dg-search-clear" onClick={clearSearch}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading Designs...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Designs could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadDashboard()}
                >
                  Retry
                </button>
              </div>
            ) : dashboard?.designs.length ? (
              <div className="dg-group-table-scroll">
                <table className="dg-group-table dg-design-table" aria-label="Designs table">
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Design</th>
                      <th>Barcode</th>
                      <th>Designs per barcode</th>
                      <th>Designs per item</th>
                      <th>Status</th>
                      <th>Chain</th>
                      <th>Created</th>
                      <th>Updated by</th>
                      <th>Crops</th>
                      <th>Reviewed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.designs.map((design) => (
                      <React.Fragment key={design.designId}>
                        <DesignRow
                          design={design}
                          isSelected={design.designId === selectedDesignId}
                          isReviewing={reviewingDesignIds.has(design.designId)}
                          canReview={hasInternalAccess}
                          imageSize={imageSettings.cropSize}
                          imageHandlers={imageSettings}
                          onSelect={() => setSelectedDesignId(design.designId)}
                          onToggleReview={() => void handleToggleDesignReview(design)}
                        />
                        {design.designId === selectedDesignId ? (
                          <tr className="dg-inline-detail-row">
                            <td colSpan={11}>
                              <DesignPanel
                                design={design}
                                imageSize={imageSettings.cropSize}
                                imageHandlers={imageSettings}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Designs match this view.</div>
            )}
          </div>
        </div>
      </section>
      {imageSettings.ZoomOverlay}
    </div>
  );
};
